import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { JobStatus, getAllJobs } from "../../Redux/Slices/JobPost";
import { formatDate } from "../../Helper/FromatTime";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import "../../asset/new/css/admin-modern.css";

const AdminPostedJobs = () => {
  const { allJobs } = useSelector((state) => state?.Jobs);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL parameters
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "approved");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [jobDetails, setJobDetails] = useState("");
  const [jobId, setJobID] = useState("");
  const [loading, setLoading] = useState(true);

  // Update URL parameters when state changes
  const updateURLParams = useCallback((tab, page, search = "") => {
    const params = new URLSearchParams();
    if (tab) params.set("tab", tab);
    if (page) params.set("page", page.toString());
    if (search) params.set("search", search);
    
    setSearchParams(params);
  }, [setSearchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to page 1 when changing tabs
    updateURLParams(tab, 1, searchQuery);
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    setCurrentPage(1); // Reset to page 1 when searching
    
    let data = {
      admin_approval: activeTab,
      page: 1,
      search: searchValue
    };
    
    dispatch(getAllJobs(data));
    updateURLParams(activeTab, 1, searchValue);
  };

  // Block and Unblock
  const userStatushandler = async (id, data) => {
    setJobID(id);
    setJobDetails(data);
  };

  const submitJobDetails = async () => {
    let action = { action: jobDetails };
    await JobStatus(jobId, action);
    
    // Maintain current page and search after status update
    let data = {
      admin_approval: activeTab,
      page: currentPage,
      search: searchQuery
    };
    dispatch(getAllJobs(data));
  };

  // Navigate to job detail while preserving current state
  const navigateToJobDetail = (jobId) => {
    // Store current state in URL before navigating
    updateURLParams(activeTab, currentPage, searchQuery);
    navigate(`/admin/reviewJob/${jobId}`);
  };

  const navigateToApplicants = (jobId) => {
    updateURLParams(activeTab, currentPage, searchQuery);
    navigate(`/admin/job-applicants/${jobId}`);
  };

  const navigateToEditJob = (jobId) => {
    updateURLParams(activeTab, currentPage, searchQuery);
    navigate(`/admin/editjob/${jobId}`);
  };

  useEffect(() => {
    // Load data based on URL parameters or defaults
    let data = {
      admin_approval: activeTab,
      page: currentPage,
    };
    
    if (searchQuery) {
      data.search = searchQuery;
    }
    
    dispatch(getAllJobs(data));
    document.title = "Posted Jobs | Admin";
  }, []);

  // Jobs According to Tab and Page
  const fetchData = useCallback(async () => {
    setLoading(true);
    let data = {
      admin_approval: activeTab,
      page: currentPage
    };
    
    if (searchQuery) {
      data.search = searchQuery;
    }
    
    await dispatch(getAllJobs(data));
    setLoading(false);
  }, [activeTab, currentPage, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pagination
  const handlePageChange = async (page) => {
    setCurrentPage(page);
    updateURLParams(activeTab, page, searchQuery);
    
    let data = {
      admin_approval: activeTab,
      page: page,
    };
    
    if (searchQuery) {
      data.search = searchQuery;
    }
    
    dispatch(getAllJobs(data));
  };

  // Calculate visible page range
  let startPage = 1;
  let endPage = 1;

  if (allJobs?.pagination) {
    startPage = Math.max(1, allJobs.pagination.page - 2);
    endPage = Math.min(
      allJobs.pagination.totalPages,
      allJobs.pagination.page + 2
    );

    // Adjust start and end page if close to beginning or end
    if (allJobs.pagination.page <= 3) {
      endPage = Math.min(5, allJobs.pagination.totalPages);
    } else if (allJobs.pagination.page >= allJobs.pagination.totalPages - 2) {
      startPage = Math.max(1, allJobs.pagination.totalPages - 4);
    }
  }

  return (
    <AdminLayout
      title="Posted Jobs"
      subtitle="Manage all job postings"
      actions={
        <button
          onClick={() => navigate("/admin/postjob")}
          type="button"
          className="admin-btn admin-btn-primary"
        >
          <i className="fas fa-plus me-2"></i>
          Post New Job
        </button>
      }
    >
      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <input
            type="search"
            id="form1"
            placeholder="Search jobs by title, company, or industry..."
            className="admin-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e)}
            style={{ maxWidth: '500px' }}
          />
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="admin-card">
        <div style={{ padding: 0, borderBottom: 'none' }}>
          <nav>
            <div className="admin-tabs" style={{ borderBottom: '2px solid var(--admin-border)', margin: 0 }}>
              <Link
                className={`admin-tab ${activeTab === "approved" ? "active" : ""}`}
                onClick={() => handleTabChange("approved")}
              >
                Approved
              </Link>
              <Link
                className={`admin-tab ${activeTab === "pending" ? "active" : ""}`}
                onClick={() => handleTabChange("pending")}
              >
                Pending
              </Link>
            </div>
          </nav>
        </div>

        <div className="admin-card-body">
          {/* Approved Tab Content */}
          {activeTab === "approved" && (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table className="admin-table" style={{ minWidth: '900px', width: '100%' }}>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Job Title</th>
                    <th>Posted By</th>
                    <th>Posted Date</th>
                    <th>Industry</th>
                    <th>Apply Through</th>
                    <th>Applicants</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <LoadingSkeleton type="table-row" count={5} columns={9} />
                  ) : allJobs?.docs && allJobs.docs.length > 0 ? (
                    allJobs.docs.map((value, index) => (
                      <tr key={index}>
                        <td>
                          <span className="admin-badge admin-badge-light">
                            {(allJobs.pagination.page - 1) * allJobs.pagination.limit + index + 1}
                          </span>
                        </td>
                        <td>
                          <div className="admin-text-truncate" style={{ maxWidth: "200px" }}>
                            {value?.title}
                          </div>
                        </td>
                        <td>{value?.created_by?.name || "N/A"}</td>
                        <td>{formatDate(value?.posted_date)}</td>
                        <td>{value?.industry?.name || "N/A"}</td>
                        <td>
                          {value?.apply_through === "Form" && value?.form_link && (
                            <a href={value?.form_link} target="_blank" rel="noopener noreferrer" className="admin-link">
                              {value?.apply_through}
                            </a>
                          )}
                          {value?.apply_through === "Website" && value?.website_link && (
                            <a href={value?.website_link} target="_blank" rel="noopener noreferrer" className="admin-link">
                              {value?.apply_through}
                            </a>
                          )}
                          {value?.apply_through === "Resume" && value?.apply_through}
                        </td>
                        <td>
                          <span className="admin-badge admin-badge-info">
                            {value?.applications?.length || 0}
                          </span>
                        </td>
                        <td>{value?.views || 0}</td>
                        <td>
                          <div className="admin-btn-group">
                            <button
                              onClick={() => navigateToJobDetail(value?._id)}
                              className="admin-btn admin-btn-sm admin-btn-info"
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              onClick={() => navigateToApplicants(value?._id)}
                              className="admin-btn admin-btn-sm admin-btn-primary"
                              title="View Applicants"
                            >
                              <i className="fas fa-users"></i>
                            </button>
                            <button
                              onClick={() => navigateToEditJob(value?._id)}
                              className="admin-btn admin-btn-sm admin-btn-warning"
                              title="Edit Job"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {value?.is_active ? (
                              <button
                                onClick={() => userStatushandler(value?._id, "false")}
                                data-bs-toggle="modal"
                                data-bs-target="#deactivateJobModal"
                                className="admin-btn admin-btn-sm admin-btn-danger"
                                title="Deactivate"
                              >
                                <i className="fas fa-ban"></i>
                              </button>
                            ) : (
                              <button
                                onClick={() => userStatushandler(value?._id, "true")}
                                data-bs-toggle="modal"
                                data-bs-target="#deactivateJobModal"
                                className="admin-btn admin-btn-sm admin-btn-success"
                                title="Activate"
                              >
                                <i className="fas fa-check-circle"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="admin-empty-state">
                          <i className="fas fa-briefcase fa-3x mb-3"></i>
                          <h4>No Approved Jobs</h4>
                          <p>There are no approved jobs at the moment.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Tab Content */}
          {activeTab === "pending" && (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table className="admin-table" style={{ minWidth: '900px', width: '100%' }}>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Job Title</th>
                    <th>Posted By</th>
                    <th>Posted Date</th>
                    <th>Industry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <LoadingSkeleton type="table-row" count={5} columns={6} />
                  ) : allJobs?.docs && allJobs.docs.length > 0 ? (
                    allJobs.docs.map((value, index) => (
                      <tr key={index}>
                        <td>
                          <span className="admin-badge admin-badge-light">
                            {(allJobs.pagination.page - 1) * allJobs.pagination.limit + index + 1}
                          </span>
                        </td>
                        <td>
                          <div className="admin-text-truncate" style={{ maxWidth: "250px" }}>
                            {value?.title}
                          </div>
                        </td>
                        <td>{value?.created_by?.name || "N/A"}</td>
                        <td>{formatDate(value?.posted_date)}</td>
                        <td>{value?.industry?.name || "N/A"}</td>
                        <td>
                          <button
                            onClick={() => navigateToJobDetail(value?._id)}
                            className="admin-btn admin-btn-sm admin-btn-info"
                          >
                            <i className="fas fa-eye me-1"></i>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="admin-empty-state">
                          <i className="fas fa-clipboard-check fa-3x mb-3"></i>
                          <h4>No Pending Jobs</h4>
                          <p>There are no pending job requests at the moment.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {allJobs?.pagination && allJobs.docs && allJobs.docs.length > 0 && (
            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                onClick={() => handlePageChange(allJobs.pagination.prevPage)}
                disabled={!allJobs.pagination.prevPage}
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>

              <div className="admin-pagination-numbers">
                {Array.from(
                  { length: Math.min(allJobs.pagination.totalPages, 5) },
                  (_, i) => {
                    const startPage = Math.max(1, allJobs.pagination.page - 2);
                    return startPage + i;
                  }
                )
                  .filter((page) => page <= allJobs.pagination.totalPages)
                  .map((page) => (
                    <button
                      key={page}
                      className={`admin-pagination-number ${
                        allJobs.pagination.page === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
              </div>

              <button
                className="admin-pagination-btn"
                onClick={() => handlePageChange(allJobs.pagination.nextPage)}
                disabled={!allJobs.pagination.nextPage}
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <div
        className="modal fade"
        id="deactivateJobModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Action</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <p>Are you sure you want to proceed with this action?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                onClick={submitJobDetails}
                type="button"
                className="admin-btn admin-btn-primary"
                data-bs-dismiss="modal"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPostedJobs;